import PageScaffold from '../../components/PageScaffold';

import { useContext, useEffect, useState } from 'react';
import DomainsContext from '../../contexts/data/DomainsContext';
import { PlusOutlined, ReloadOutlined, TagsOutlined, GlobalOutlined } from '@ant-design/icons';
import DomainsList from '../../components/domains_page/DomainsList';
import TagsList from '../../components/domains_page/TagsList';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { UserRole, ContextStatus, TagCategory } from '@leedpulse-monorepo/enums';
import { Flex, Segmented, Tabs, Typography } from 'antd';

export const DOMAINS_PAGE_PATH = '/domains';
export const DOMAINS_PAGE_TITLE = 'Domaines';

const ARCHIVED_QUERYKEY = 'archived';

type TDomainArchivedFilter = 'all' | 'not_archived' | 'archived';

const parseArchivedFromUrl = (value: string | null): TDomainArchivedFilter =>
  value === 'all' || value === 'archived' ? value : 'not_archived';

const TAB_KEYS = {
  domains: '1',
  tags: '2',
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface DomainsPageProps {}

const DomainsPage: React.FC<DomainsPageProps> = () => {
  const domainsContext = useContext(DomainsContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(TAB_KEYS.domains);
  const [searchParams, setSearchParams] = useSearchParams();
  const archivedFilter = parseArchivedFromUrl(searchParams.get(ARCHIVED_QUERYKEY));

  const authContext = useContext(AuthenticationContext);

  useEffect(() => {
    // Always reloads: another page can leave the shared context in SUCCESS with no items (fetchBySubscription)
    domainsContext.fetch(true);
    return () => domainsContext.reset();
  }, []);

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin].includes(
      authContext.authentication?.role
    );

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const setArchivedFilterInUrl = (filter: TDomainArchivedFilter) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(ARCHIVED_QUERYKEY, filter);
        return next;
      },
      { replace: true }
    );
  };

  // Every domain is loaded (a dozen docs, no Algolia index): the archived filter runs here
  const domains = domainsContext.items.filter(
    (domain) => archivedFilter === 'all' || domain.archived === (archivedFilter === 'archived')
  );

  return (
    <PageScaffold
      title={
        DOMAINS_PAGE_TITLE + ' (' + formatNumber(domains.length) + ')'
      }
      subtitle={'Gérez les domaines'}
      breadcrumbItems={[
        {
          title: 'Domaines',
        },
      ]}
      controlsSettings={{
        searchLabel: '',
        onSearch: (_) => {return;},
        onSearchCancel: () => {return;},
        hideSearch: true,
        isLoading: false,
        actionButtons: !isReadOnly
          ? [
              {
                label: 'Nouveau domaine',
                type: 'primary',
                props: {
                  icon: <PlusOutlined />,
                  onClick: () => navigate('new'),
                },
              },
              {
                label: 'Rafraîchir',
                type: 'primary',
                props: {
                  icon: <ReloadOutlined />,
                  onClick: () => domainsContext.fetch(true),
                },
              },
            ]
          : [
              {
                label: <ReloadOutlined />,
                type: 'primary',
                props: {
                  onClick: () => domainsContext.fetch(true),
                },
              },
            ],
      }}
    >
      <Tabs
        defaultActiveKey={activeTab}
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: TAB_KEYS.domains,
            label: 'Domaines',
            icon: <GlobalOutlined />,
            children: (
              <>
                <Flex gap={24} wrap="wrap" align="flex-end" style={{ margin: '0 0 1rem 0.5rem' }}>
                  <Flex vertical gap={4}>
                    <Typography.Text type="secondary">Domaine</Typography.Text>
                    <Segmented<TDomainArchivedFilter>
                      value={archivedFilter}
                      onChange={(v) => setArchivedFilterInUrl(v)}
                      options={[
                        { label: 'Tout', value: 'all' },
                        { label: 'Non archivés', value: 'not_archived' },
                        { label: 'Archivés', value: 'archived' },
                      ]}
                    />
                  </Flex>
                </Flex>
                <DomainsList domains={domains} loading={domainsContext.status === ContextStatus.LOADING} />
              </>
            ),
          },
          {
            key: TAB_KEYS.tags,
            label: 'Tags globaux',
            icon: <TagsOutlined />,
            children: <TagsList domainId={null} subscriptionId={null} filterCategories={[TagCategory.typology, TagCategory.heat, TagCategory.status]} defaultCategory={TagCategory.typology} allEditable />,
          },
        ]}
      />
    </PageScaffold>
  );
};

export default DomainsPage;
